import { ReactText } from 'react';
import nextId from 'react-id-generator';
import { IMap, Nullable } from '@symfa-inc/providence-verizon-types';
import { generatedIdMapper, transformFields } from '../../core/utils/methods';
import { createReducer, on } from '../../core/utils/store';
import {
  LEASING_INFO_TRANSFORM_FIELDS,
  STRUCTURAL_INFO_TRANSFORM_FIELDS,
} from '../../models/constants/transform-fields';
import {
  AssociatedProject,
  EquipmentModal,
  FinancialData,
  IdAndValue,
  LeasingBrowseItem,
  LeasingBrowseResponseItem,
  LeasingDetails,
  LeasingEquipmentResp,
  LeasingEquipmentTabData,
  LeasingEquipmentUpdateData,
  LeasingInformation,
  LeasingLeaseAuditTabData,
  LeasingSummaryTabData,
  LeasingWorkflowTabData,
  LeasingWorkflowUpdateData,
  OptionProps,
  PaginatedResponse,
  PayloadAndState,
  PermittingJurisdictionAuditData,
  ProjectDetails,
  ScopingSummaryTabData,
  SiteResponse,
  StructuralInformation,
} from '../../models/interfaces';
import {
  CuttedProjectDetails,
  LeasingEquipmentData,
  LeasingLeaseAuditUpdateData,
} from '../../models/types';
import { ScopingActions } from '../actions/engineering/scoping.actions';
import { LeasingActions, LeasingActionType } from '../actions/leasing.actions';
import {
  getEquipmentTotalsFromScoping,
  getProcessedEquipments,
  sortEquipments,
} from './helpers';

type StoreLeasingInformation = Partial<LeasingInformation> & {
  leasingNote: Nullable<string>;
};

export interface LeasingState {
  leasingBrowseData: PaginatedResponse<LeasingBrowseItem>;
  leasingDetailsData: LeasingDetails;
  //
  associatedProjects: AssociatedProject[];
  currentProject: CuttedProjectDetails;
  leasingSite: SiteResponse;
  leasingInformation: StoreLeasingInformation;
  leasingAgents: OptionProps[];
  leasingEquipmentData: LeasingEquipmentData;
  equipmentTypeNames: string[];
  isFetching: boolean;
  errors: boolean;

  loadingTabs: Record<string, boolean>;
  loadedTabs: Record<string, boolean>;
  tabData: {
    summary: LeasingSummaryTabData;
    workflow: LeasingWorkflowTabData;
    leaseAudit: LeasingLeaseAuditTabData;
    equipment: LeasingEquipmentTabData;
    jurisdictionAudit: PermittingJurisdictionAuditData;
    financial: FinancialData;
  };
}

const initialState: LeasingState = {
  leasingBrowseData: {
    items: [],
    total: 0,
  },
  leasingDetailsData: {} as LeasingDetails,
  //
  associatedProjects: [],
  currentProject: {} as Omit<ProjectDetails, 'fuzeIDs' | 'paceTasks' | 'site'>,
  leasingSite: {} as SiteResponse,
  leasingInformation: {} as StoreLeasingInformation,
  leasingAgents: [],
  leasingEquipmentData: {
    priorTableData: [],
    curPriorTableData: [],
    scopingTableData: [],
    finalTableData: [],
    curFinalTableData: [],
    scopingNotes: null,
    priorLeasingNote: null,
    finalLeasingNote: null,
  },
  equipmentTypeNames: [],
  isFetching: false,
  errors: false,
  loadingTabs: {},
  loadedTabs: {},
  tabData: {
    summary: {} as LeasingSummaryTabData,
    workflow: {} as LeasingWorkflowTabData,
    leaseAudit: {} as LeasingLeaseAuditTabData,
    equipment: {} as LeasingEquipmentTabData,
    jurisdictionAudit: {} as PermittingJurisdictionAuditData,
    financial: {} as FinancialData,
  },
};

const equipmentTypeNames = new Set<string>();

const removeNullableValues = <T>(obj: T, keys: Array<keyof T>) =>
  keys.reduce(
    (acc: IMap<T[keyof T]>, key: keyof T) => ({
      ...acc,
      ...(obj[key] != null ? { [key]: obj[key] } : {}),
    }),
    {},
  );

export const reducer = createReducer(
  initialState,
  // GET LEASING BROWSE
  on(
    LeasingActions.getLeasingBrowseDataAction,
    ({
      payload,
    }: PayloadAndState<
      PaginatedResponse<LeasingBrowseResponseItem>,
      LeasingState
    >) => ({
      leasingBrowseData: {
        ...payload,

        items: payload.items.map(({ categories, ...item }) => ({
          ...item,
          fuzeIDs: categories,
        })),
      },
    }),
  ),

  // GET SCOPING SUMMARY
  on(
    ScopingActions.getScopingSummaryAction,
    ({
      payload: { site, leasing: leasingPayload, ...currentProject },
      state: { leasingInformation },
    }: PayloadAndState<
      Omit<ScopingSummaryTabData, 'categories' | 'RAD' | 'providenceAgent'>,
      LeasingState
    >) => {
      const leasing = leasingPayload ?? {};
      const { structural, ...restLeasingData } = leasing;

      return {
        currentProject: currentProject as unknown as CuttedProjectDetails,
        leasingSite: transformFields<SiteResponse>(site as SiteResponse, [
          'towerOwner',
          'riskCategory',
        ]),
        leasingInformation: {
          ...leasingInformation,
          ...transformFields<LeasingInformation>(
            restLeasingData as LeasingInformation,
            LEASING_INFO_TRANSFORM_FIELDS,
          ),
          structural: structural
            ? transformFields<StructuralInformation>(
                structural,
                STRUCTURAL_INFO_TRANSFORM_FIELDS,
              )
            : undefined,
        },
      };
    },
  ),
  // GET LEASING EQUIPMENT PRIOR PROJECT
  on(
    LeasingActions.getLeasingEquipmentPriorProjectAction,
    ({
      payload: { scoping: priorScopingData, leasing },
      state: { leasingEquipmentData },
    }: PayloadAndState<LeasingEquipmentResp, LeasingState>) => {
      const scopingData = priorScopingData ?? {};

      const curPriorTableData = getEquipmentTotalsFromScoping(
        equipmentTypeNames,
        scopingData,
        'PriorEquipment',
      );

      return {
        leasingEquipmentData: {
          ...leasingEquipmentData,
          curPriorTableData,
          curFinalTableData: sortEquipments(
            leasingEquipmentData.scopingTableData.map(
              (equipment: EquipmentModal) => ({
                ...equipment,
                generatedId: nextId(),
              }),
            ),
          ),
          priorLeasingNote: leasing?.priorLeasingNote,
          finalLeasingNote: null,
        },
        equipmentTypeNames: [...equipmentTypeNames],
      };
    },
  ),
  // UPDATE LEASING WORKFLOW
  on(
    LeasingActions.updateLeasingWorkflowAction,
    ({
      payload: { engineering, leasing, structural },
      state,
    }: PayloadAndState<LeasingWorkflowUpdateData, LeasingState>) => ({
      tabData: {
        ...state.tabData,
        workflow: {
          ...state.tabData.workflow,
          engineering: {
            ...state.tabData.workflow.engineering,
            ...engineering,
          },
          leasing: { ...state.tabData.workflow.leasing, ...leasing },
          structural: state.tabData.workflow.structural
            ? { ...state.tabData.workflow.structural, ...structural }
            : (structural as unknown as LeasingWorkflowTabData['structural']) ??
              null,
        },
      },
    }),
  ),
  // UPDATE LEASE AUDIT
  on(
    LeasingActions.updateLeaseAuditAction,
    ({
      payload: {
        area,
        leasingNote,
        mostRecentSATIARevCodeId,
        agreementTypeId,
        leaseAuditBy,
        ...payloadData
      },
      state: { leasingInformation, tabData },
    }: PayloadAndState<
      Partial<LeasingLeaseAuditUpdateData> & {
        area: 'engineering' | 'leasing';
      },
      LeasingState
    >) =>
      area === 'engineering'
        ? {
            leasingInformation: {
              ...leasingInformation,
              ...(leasingNote ? { leasingNote } : {}),
              leaseAudit: {
                ...leasingInformation.leaseAudit,
                ...(agreementTypeId !== undefined && {
                  agreementType: agreementTypeId
                    ? ({ id: agreementTypeId } as IdAndValue)
                    : null,
                }),
                ...(mostRecentSATIARevCodeId !== undefined && {
                  mostRecentSATIARevCode: mostRecentSATIARevCodeId
                    ? ({ id: mostRecentSATIARevCodeId } as IdAndValue)
                    : null,
                }),
                ...(leaseAuditBy !== undefined && {
                  leaseAuditBy,
                }),
                ...removeNullableValues(payloadData, [
                  'documentAudited',
                  'documentDate',
                  'currentRentAmount',
                  'escalator',
                  'termLengthOrExtension',
                  'currentTermStartDate',
                  'currentTermEndDate',
                  'finalTermEndDate',
                  'mostRecentSADate',
                  'mostRecentTowerSAPercentage',
                  'isMostRecentAdequate',
                  'mostRecentFoundationSAPercentage',
                  'separateGeneratorSpace',
                  'generatorDetails',
                  'azimuthA',
                  'azimuthB',
                  'azimuthD',
                  'azimuthG',
                ]),
              },
            },
          }
        : {
            leasingInformation: {
              ...leasingInformation,
              ...(leasingNote ? { leasingNote } : {}),
            },
            tabData: {
              ...tabData,
              leaseAudit: {
                ...tabData.leaseAudit,
                ...(mostRecentSATIARevCodeId !== undefined && {
                  mostRecentSATIARevCodeId,
                }),
                ...(agreementTypeId !== undefined && {
                  agreementTypeId,
                }),
                ...(leaseAuditBy !== undefined && {
                  leaseAuditBy,
                }),
                ...(leasingNote !== undefined && {
                  leasingNote,
                }),
                ...payloadData,
              },
            },
          },
  ),
  // ADD LEASING EQUIPMENT TABLE ITEM
  on(
    LeasingActions.addTableItemAction,
    ({
      payload: { data, key },
      state: { leasingEquipmentData },
    }: PayloadAndState<LeasingActionType, LeasingState>) => ({
      leasingEquipmentData: {
        ...leasingEquipmentData,
        [key]: sortEquipments([...leasingEquipmentData[key], data]),
      },
    }),
  ),
  // UPDATE LEASING EQUIPMENT TABLE ITEM
  on(
    LeasingActions.editTableItemAction,
    ({
      payload: { data, key },
      state: { leasingEquipmentData },
    }: PayloadAndState<LeasingActionType, LeasingState>) => {
      const dataToEdit = leasingEquipmentData[key];

      const index = dataToEdit.findIndex(
        (equipment: EquipmentModal) =>
          equipment.generatedId === data.generatedId,
      );

      const editedData = [...dataToEdit];

      editedData.splice(index, 1, data);

      return {
        leasingEquipmentData: {
          ...leasingEquipmentData,
          [key]: sortEquipments(editedData),
        },
      };
    },
  ),
  // REMOVE LEASING EQUIPMENT TABLE ITEM
  on(
    LeasingActions.deleteTableItemAction,
    ({
      payload: { data, key },
      state: { leasingEquipmentData },
    }: PayloadAndState<LeasingActionType, LeasingState>) => {
      const revertKey =
        key === 'curPriorTableData' ? 'curFinalTableData' : 'curPriorTableData';
      const dataToEdit = leasingEquipmentData[revertKey];

      const index = dataToEdit.findIndex(
        (equipmentModalItem: EquipmentModal) =>
          equipmentModalItem.generatedId === data.generatedId ||
          equipmentModalItem.equipment === data.equipment,
      );

      const editedData = [...dataToEdit];

      if (index >= 0) {
        editedData.splice(index, 1, {
          ...editedData[index],
          isLeaseRight: false,
        });
      }

      return {
        leasingEquipmentData: {
          ...leasingEquipmentData,
          [key]: [
            ...leasingEquipmentData[key].filter(
              (equipment: EquipmentModal) =>
                equipment.generatedId !== data.generatedId,
            ),
          ],
          [revertKey]: index < 0 ? leasingEquipmentData[revertKey] : editedData,
        },
      };
    },
  ),
  // UPDATE LEASING EQUIPMENT
  on(
    LeasingActions.updateLeasingEquipmentAction,
    ({
      payload,
      state: { leasingEquipmentData },
    }: PayloadAndState<LeasingEquipmentUpdateData, LeasingState>) => ({
      leasingEquipmentData: {
        ...leasingEquipmentData,
        ...payload,
        curPriorTableData: generatedIdMapper(
          payload.priorTableData,
          'PriorEquipment',
        ),
        curFinalTableData: generatedIdMapper(
          payload.finalTableData,
          'FinalEquipment',
        ),
      },
    }),
  ),
  // EMPTY A PRIOR TABLE
  on(
    LeasingActions.emptyPriorTableAction,
    ({
      state: { leasingEquipmentData },
    }: PayloadAndState<null, LeasingState>) => ({
      leasingEquipmentData: {
        ...leasingEquipmentData,
        curPriorTableData: [],
      },
    }),
  ),
  // GENERATE LEASING EQUIPMENT FINAL TABLE DATA
  on(
    LeasingActions.generateFinalLoadingAction,
    ({
      payload,
      state: { leasingEquipmentData },
    }: PayloadAndState<ReactText[], LeasingState>) => ({
      leasingEquipmentData: {
        ...leasingEquipmentData,
        curPriorTableData: sortEquipments(
          leasingEquipmentData.curPriorTableData.map(
            (priorEquipment: EquipmentModal) => ({
              ...priorEquipment,
              ...{
                isLeaseRight: payload.includes(
                  priorEquipment?.generatedId || '',
                ),
              },
            }),
          ),
        ),
        curFinalTableData: sortEquipments([
          ...leasingEquipmentData.scopingTableData,
          ...leasingEquipmentData.curPriorTableData
            .filter((priorEquipment: EquipmentModal) =>
              payload.includes(priorEquipment?.generatedId || ''),
            )
            .map((priorEquipment: EquipmentModal) => ({
              ...priorEquipment,
              isLeaseRight: true,
            })),
        ]),
      },
    }),
  ),
  // RESET LEASING EQUIPMENT TABLES DATA
  on(
    LeasingActions.resetTablesDataAction,
    ({
      state: { leasingEquipmentData },
    }: PayloadAndState<null, LeasingState>) => ({
      leasingEquipmentData: {
        ...leasingEquipmentData,
        curPriorTableData: [...leasingEquipmentData.priorTableData],
        curFinalTableData: [...leasingEquipmentData.finalTableData],
      },
    }),
  ),

  // SET TAB LOADING STATE
  on(
    LeasingActions.setTabLoading,
    ({
      payload,
      state,
    }: PayloadAndState<
      { tabName: string; isLoading: boolean },
      LeasingState
    >) => ({
      ...state,
      loadingTabs: {
        ...state.loadingTabs,
        [payload.tabName]: payload.isLoading,
      },
    }),
  ),
  // RESET TABS DATA (on navigation away from details)
  on(LeasingActions.resetTabsData, ({ state }) => ({
    ...state,
    tabData: initialState.tabData,
    loadingTabs: {},
    loadedTabs: {},
  })),
  // GET SUMMARY TAB DATA
  on(
    LeasingActions.getSummaryTabDataAction,
    ({
      payload,
      state,
    }: PayloadAndState<LeasingSummaryTabData, LeasingState>) => ({
      ...state,
      leasingSite: transformFields<SiteResponse>(
        payload.site as unknown as SiteResponse,
        ['towerOwner', 'riskCategory'],
      ),
      tabData: {
        ...state.tabData,
        summary: payload,
      },
      loadingTabs: { ...state.loadingTabs, summary: false },
      loadedTabs: { ...state.loadedTabs, summary: true },
    }),
  ),
  // GET WORKFLOW TAB DATA
  on(
    LeasingActions.getWorkflowTabDataAction,
    ({
      payload,
      state,
    }: PayloadAndState<LeasingWorkflowTabData, LeasingState>) => ({
      ...state,
      tabData: {
        ...state.tabData,
        workflow: payload,
      },
      loadingTabs: { ...state.loadingTabs, workflow: false },
      loadedTabs: { ...state.loadedTabs, workflow: true },
    }),
  ),
  // GET LEASE AUDIT TAB DATA
  on(
    LeasingActions.getLeaseAuditTabDataAction,
    ({
      payload,
      state,
    }: PayloadAndState<LeasingLeaseAuditTabData, LeasingState>) => ({
      ...state,
      leasingAgents: payload.leasingAgents as unknown as OptionProps[],
      tabData: {
        ...state.tabData,
        leaseAudit: payload,
      },
      loadingTabs: { ...state.loadingTabs, leaseAudit: false },
      loadedTabs: { ...state.loadedTabs, leaseAudit: true },
    }),
  ),
  // GET EQUIPMENT TAB DATA
  on(
    LeasingActions.getEquipmentTabDataAction,
    ({
      payload,
      state,
    }: PayloadAndState<LeasingEquipmentTabData, LeasingState>) => ({
      leasingEquipmentData: {
        priorTableData: getProcessedEquipments(
          equipmentTypeNames,
          payload.leasingEquipments,
          'PriorEquipment',
        ),
        curPriorTableData: getProcessedEquipments(
          equipmentTypeNames,
          payload.leasingEquipments,
          'PriorEquipment',
        ),
        finalTableData: getProcessedEquipments(
          equipmentTypeNames,
          payload.leasingFinalEquipments,
          'FinalEquipment',
        ),
        curFinalTableData: getProcessedEquipments(
          equipmentTypeNames,
          payload.leasingFinalEquipments,
          'FinalEquipment',
        ),
        scopingTableData: sortEquipments(
          getEquipmentTotalsFromScoping(
            equipmentTypeNames,
            payload.scoping,
            'ScopingEquipment',
          ),
        ),
        scopingNotes: payload.scoping.scopingTowerNote,
        priorLeasingNote: payload.priorLeasingNote,
        finalLeasingNote: payload.finalLeasingNote,
      },
      equipmentTypeNames: [...equipmentTypeNames],
      tabData: {
        ...state.tabData,
        equipment: payload,
      },
      loadingTabs: { ...state.loadingTabs, equipment: false },
      loadedTabs: { ...state.loadedTabs, equipment: true },
    }),
  ),
  // Load jurisdiction audit tab data. Main data in permitting store
  on(LeasingActions.getJurisdictionAuditTabData, ({ state }) => {
    return {
      ...state,
      loadingTabs: { ...state.loadingTabs, jurisdictionAudit: false },
      loadedTabs: { ...state.loadedTabs, jurisdictionAudit: true },
    };
  }),

  // Load financial tab data
  on(LeasingActions.getFinancialTabData, ({ state }) => {
    return {
      ...state,
      loadingTabs: { ...state.loadingTabs, financial: false },
      loadedTabs: { ...state.loadedTabs, financial: true },
    };
  }),
);
